<template>
  <v-container id="my-groups" fluid tag="section">
    <v-overlay :value="isOverlayVisible">
      <v-progress-circular
        :size="70"
        :width="7"
        color="green"
        indeterminate
        class="spinner"
      />
    </v-overlay>
    <VComponent :heading="heading"/>
    <div class="actionBar">
      <div class="rightPart">
        <dashboard-filter @on-submit="_fetchData" @on-clear="_reset" />
      </div>
    </div>
    <template>
      <v-toolbar flat color="transparent">
        <v-text-field
          v-model="searchText"
          class="searchBox"
          label="Search patients"
          @keyup="_filterPatients"
        />

        <v-spacer />

        <ivr-call-initiator
          :patients="selected.length ? selected : filteredPatients"
          :templates="templates"
          :is-visible="ivrInitiator.isVisible"
          @on-send-start="messageSendInitiated"
          @dismiss="_hideComposerDialog"
          @on-success="callInitiated"
          @on-test-success="testMessageSent"
          @on-error="messageError"
        />

        <v-btn color="primary" v-show="selected.length" dark class="mb-2" @click="initiateCall">
          <v-icon small class="mr-2">
            mdi-phone
          </v-icon>
          <span>
            Initiate Call
          </span>
        </v-btn>
        <v-btn
          color="primary"
          v-show="!selected.length && filteredPatients.length"
          dark
          class="mb-2"
          @click="initiateCall"
        >
          <v-icon small class="mr-2">
            mdi-phone
          </v-icon>
          <span>
            Initiate mass call
          </span>
        </v-btn>

        <div class="button-separator" />

        <message-composer-dialog
          :patients="selected"
          :templates="templates"
          :is-visible="messageComposer.isVisible"
          @on-send-start="messageSendInitiated"
          @dismiss="_hideComposerDialog"
          @on-success="messageSent"
          @on-test-success="testMessageSent"
          @on-error="messageError"
        />
        <v-btn color="primary" v-show="selected.length" dark class="mb-2" @click="_composeMessage">
          <v-icon small class="mr-2">
            mdi-message
          </v-icon>
          <span>
            Compose message
          </span>
        </v-btn>
        <v-btn
          color="primary"
               v-show="!selected.length && filteredPatients.length"
          dark
          class="mb-2"
          @click="_composeMessage"
        >
          <v-icon small class="mr-2">
            mdi-message
          </v-icon>
          <span>
            Compose mass message
          </span>
        </v-btn>
        <div class="button-separator" />

        <email-composer-dialog
          :patients="selected"
          :templates="templates"
          :fromEmail="this.practiceDetails.email"
          :is-visible="isEmailComposerVisible"
          @dismiss="_hideComposerDialog"
          @on-send-start="messageSendInitiated"
          @on-success="messageSent"
          @on-test-success="testMessageSent"
          @on-error="messageError"
        />
        <v-btn color="primary" v-show="selected.length" dark class="mb-2" @click="_composeEmail">
          <v-icon small class="mr-2">
            mdi-email
          </v-icon>
          <span>
            Compose email
          </span>
        </v-btn>
        <v-btn
          color="primary"
          v-show="!selected.length && filteredPatients.length"
          dark
          class="mb-2"
          @click="_composeEmail"
        >
          <v-icon small class="mr-2">
            mdi-email
          </v-icon>
          <span>
            Compose mass email
          </span>
        </v-btn>
      </v-toolbar>
    </template>

    <div v-if="!isDataLoaded" class="dateSelectorHelpText">
      <h2>Please select a date range to load data</h2>
    </div>
    <v-data-table
      v-model="selected"
      v-if="isDataLoaded"
      show-select
      :headers="activeHeaders"
      :items="filteredPatients"
      item-key="PatientID"
      sort-by="PatientID"
      @toggle-select-all="selectAllToggle"
    >
      <template v-slot:[`item.data-table-select`]="{ isSelected, select }">
        <v-simple-checkbox :value="isSelected" @input="select($event)" />
      </template>
      <template v-slot:[`item.FirstName`]="{ item }">
        <span class="nameLink" @click="onPatientRowClick(item)">
          {{ item.FirstName }}
        </span>
      </template>
      <template v-slot:[`item.LastName`]="{ item }">
        <span class="nameLink" @click="onPatientRowClick(item)">
          {{ item.LastName }}
        </span>
      </template>
      <template v-slot:[`item.ApptDate`]="{ item }">
        {{ item.ApptDate ? moment(`${item.ApptDate}.000Z`).format("hh:mm a DD-MMM-YY"): '--' }}
      </template>
      <template v-slot:[`item.ApptConfirm`]="{ item }">
        <span >
          {{item.ApptStatus}}
        </span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="senderActions">
          <router-link
            :to="{ name: 'Group Contacts', params: { groupId: item.id } }"
          >
            <v-btn color="primary" outlined x-small class="ml-4">
              View Contacts
            </v-btn>
          </router-link>
          <v-icon small @click="deleteItem(item)"> mdi-delete </v-icon>
        </div>
      </template>
      <template v-slot:no-data>
        <p class="mb-4 mt-4">No items to display</p>
        <v-btn color="primary" outlined class="mb-4" @click="_refresh">
          Refresh
        </v-btn>
      </template>
    </v-data-table>

    <v-snackbar v-model="snackbar">
      {{ notification }}

      <template v-slot:action="{ attrs }">
        <v-btn color="pink" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <div class="py-3" />
  </v-container>
</template>

<script>
  /* eslint-disable */
  import moment from "moment";
  import DateRangePicker from "vue2-daterange-picker";
  import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
  import { ApiCalls } from "../../../../api-calls";
  import UserHelper from "../../../../utils/user-helper";
  import EventEmitter from "../../../../utils/event-emitter";
  import DashboardFilter from "../../components/dashboard-filter/dashboard-filter.vue";
  import VComponent from "../../../../components/base/VComponent.vue";
  import IvrCallInitiator from "../../components/ivr-call-initiator/ivr-call-initiator.vue";
  import CommValidationDialog from "../../components/comm-validation-dialog/comm-validation-dialog.vue";
  import MessageComposerDialog from "../../components/message-composer/message-composer.vue";
  import EmailComposerDialog from "../../components/email-composer/email-composer.vue";
  import Api from "../../../shared-module/api";

  export default {
    components: {
      DashboardFilter,
      VComponent,
      CommValidationDialog,
      IvrCallInitiator,
      EmailComposerDialog,
      MessageComposerDialog,
    },
    props: {
      groupId: {
        required: true,
        type: String
      },
      fromEmail: {
        type: String,
        default: ""
      }
    },
    data: () => ({
      isEmailComposerVisible: false,
      practiceDetails: {},
      dateRange: {},
      selected: [],
      moment,
      picker: {},
      dialog: false,
      allHeaders: [
        { text: "First Name", value: "FirstName" },
        { text: "Last Name", value: "LastName" },
        { text: "Phone", value: "MobileNumber" },
        { text: "Appt time", value: "ApptDate" },
        { text: "Appt Status", value: "ApptConfirm" },
        { text: "PD $ amount", value: "PDamount" },
        { text: "Provider", value: "Provider" },
      ],
      activeHeaders: [],
      snackbar: false,
      notification: "Unknown",
      isOverlayVisible: false,
      patients: [],
      filteredPatients: [],
      isDataLoaded: false,
      searchText: "",
      activeClinicName: "",
      templates: [],
      filterTypes: [],
      filterTypeID: {
        type: Number
      },
      groupDetails: {},
      heading: "Appointment Dashboard",
      messageComposer: {
        isVisible: false,
        isBulkMode: false
      },
      emailComposer: {
        isVisible: false,
        isBulkMode: false
      },
      groupBulkEmailComposer: {
        isVisible: false,
        isBulkMode: true
      },
      commValidationPrompt: {
        isVisible: false,
        commMode: "sms"
      },
      ivrInitiator: {
        isVisible: false,
        isBulkMode: false
      },
    }),

    computed: {
      activePractice() {
        console.log('Dashboard page computed' + this.$store.state.posActivePractice);
        return this.$store.state.posActivePractice;
      }
    },

    watch: {
      dateRange(val) {
        this._initiateLoad({ startDate: val.startDate, endDate: val.endDate });
      },
      searchText() {
        this._filterPatients();
      },
    },

    async created() {
      this.usremail = localStorage.getItem("username");
      this._loadClinicDetails();
      this._generateTableHeaders();
    },

    methods: {
      _reset() {
        this.isDataLoaded = false;
        this.patients = [];
        this.filteredPatients = [];
      },
      _loadClinicDetails() {
        const activeClinic = UserHelper.getActiveClinic();
        this.activeClinicName = activeClinic.company;
      },
      _refresh() {
        this.searchText = "";
        this._initiateLoad({
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate,
        });
      },
      async _fetchData(payload) {
        this.isDataLoaded = true;
        this.isOverlayVisible = true;
        try {
          const { data } = await ApiCalls.fetchDashboardData(payload);
          this._generateTableHeaders(payload);
          this.patients = data;
          if (this.patients.length > 0) {
            this.patients.forEach((value, index) => {
              this.patients[index].PDamount = '$' + this.patients[index].PDamount;
            });
          }

          this._filterPatients();
        } catch (error) {
          this.notification = "Unable to load data";
          this.snackbar = true;
        }
        this.isOverlayVisible = false;
      },
      async _initiateLoad({ startDate, endDate }) {
        this.isDataLoaded = true;
        this.isOverlayVisible = true;
        try {
          if (startDate && endDate) {
            const { data } = await ApiCalls.fetchDashboardData({
              startDate: moment(startDate).format("MM/DD/YYYY"),
              endDate: moment(endDate).format("MM/DD/YYYY"),
            });
            this.patients = data;
            this._filterPatients();
          }
        } catch (error) {
          this.notification = "Unable to load data";
          this.snackbar = true;
        }
        this.isOverlayVisible = false;
      },
      _generateTableHeaders(payload = null) {
        this.activeHeaders = Object.assign([], this.allHeaders);
        // payload.type = selectedType from dashboard filter.
        switch (payload.type) {
          case 1:
            this.heading = 'Appointment Dashboard';
            this.activeHeaders.splice(5, 1);
            break;
          case 2:
            this.heading = 'Financial Dashboard';
            this.activeHeaders.splice(3, 1);
            this.activeHeaders.splice(3, 1);
            this.activeHeaders.splice(4, 1);
            break;
          default:
            this.heading = 'Appointment Dashboard';
            break;
        }
      },
      _filterPatients() {
        this.filteredPatients = [...this.patients];
        const searchText = this.searchText.trim().toLowerCase();
        if (searchText && searchText.length) {
          // eslint-disable-next-line array-callback-return
          this.filteredPatients = this.filteredPatients.filter((c) => {
            if (
              c.FirstName &&
              c.FirstName.length &&
              c.FirstName.toLowerCase().indexOf(searchText) > -1
            ) {
              return true;
            }
            if (
              c.LastName &&
              c.LastName.length &&
              c.LastName.toLowerCase().indexOf(searchText) > -1
            ) {
              return true;
            }
            if (
              c.Provider &&
              c.Provider.length &&
              c.Provider.toLowerCase().indexOf(searchText) > -1
            ) {
              return true;
            }
            if (
              c.phone &&
              c.phone.length &&
              c.phone.toLowerCase().indexOf(searchText) > -1
            ) {
              return true;
            }
          });
        }
      },
      _updateValues() {
        if (this.dateRange.startDate && this.dateRange.endDate) {
          // Fetch data for the new date range
          this._initiateLoad();
        }
      },
      onPatientRowClick(row) {
        EventEmitter.dispatch("show-patient-details", {
          ContactID: row.PatientID,
          CellNumber: row.MobileNumber,
          MedicalGroupID: row.MedicalGroupID
        });
      },
      close() {
        this.dialog = false;
        this.$nextTick(() => {
          this.editedItem = {
            id: -1,
            name: "",
            members: "-",
            messages: "-",
          };
          this.editedIndex = -1;
          this.selectedSenderID = 0;
          this.isFormDirty = false;
        });
      },

      /* Message Composers */
      initiateCall() {
        this.ivrInitiator = {
          isVisible: true,
          isBulkMode: false
        };
      },
      _hideComposerDialog() {
        this.messageComposer = {
          isVisible: false,
          isBulkMode: false
        };
        this.emailComposer = {
          isVisible: false,
          isBulkMode: false
        };
        this.groupBulkEmailComposer = {
          isVisible: false,
          isBulkMode: true
        };
        this.ivrInitiator = {
          isVisible: false,
          isBulkMode: false
        };
        this.isEmailComposerVisible = false;
      },
      callInitiated() {
        this.isOverlayVisible = false;
        this.notification = "Call has been initiated";
        this.snackbar = true;
      },
      messageError(error) {
        let errorMessage = "";
        if (error && error.response && error.response.data && error.response.data.length) {
          errorMessage = error.response.data;
        } else {
          errorMessage = "Unable to send the message";
        }
        this.notification = errorMessage;
        this.isOverlayVisible = false;
        this.snackbar = true;
        throw error;
      },
      messageSendInitiated() {
        this.isOverlayVisible = true;
      },
      messageSent() {
        this.isOverlayVisible = false;
        this.notification = "Message has been sent";
        this.snackbar = true;
      },
      testMessageSent() {
        this.isOverlayVisible = false;
        this.notification = "Test message has been sent";
        this.snackbar = true;
      },
      selectAllToggle() {
        if (this.selected.length < this.filteredPatients.length) {
          // Currently selectable rows count is greater than selected rows
          // So select all
          this.selected = [];
          const self = this;
          this.filteredPatients.forEach(item => {
            self.selected.push(item);
          });
        } else {
          // Currently selectable rows count is equal to selected rows
          // So de-select all
          this.selected = [];
        }
      },
      _composeMessage() {
        this.messageComposer = {
          isVisible: true,
          commMode: "sms"
        };
      },
      _composeEmail() {
        this._fetchPracticeInfo(activeClinic.PracticeID);
        this.isEmailComposerVisible = true;
      },
      _hideCommValidationDialog() {
        this.isEmailComposerVisible = false;
      },
      _updateSelctionAndShowComposer(updatedPatients) {
        this.selected = updatedPatients;
        if (this.commValidationPrompt.commMode === "sms") {
          this.messageComposer = {
            isVisible: true,
            isBulkMode: false
          };
        } else {
          // Email
          this.emailComposer = {
            isVisible: true,
            isBulkMode: false
          };
        }
        this._hideCommValidationDialog();
      },
      /* Message Composers End */
    },
  };
</script>

<style scoped>
.v-overlay.v-overlay--active.theme--dark {
  z-index: 300 !important;
}
.spinner {
  margin-left: 17rem;
}
@media only screen and (max-width: 968px) {
  .spinner {
    margin-left: 0;
  }
}
#bottomTable {
  margin-top: 3rem;
}
</style>

<style lang="scss">
.actionBar {
  display: flex;
  margin-bottom: 3rem;
  .leftPart {
    flex: 1;
    padding-top: 15px;
    .searchBox {
      padding: 2px 15px;
      width: 100%;
      border-bottom: 1px solid black;
      color: black;
      outline: none;
    }
  }
  .rightPart {
    flex: 2;
    display: flex;
    justify-content: flex-end;
    .dateRangePickerWrapper {
      width: 221px;
      // height: 40px;
      background-color: white;
      border: 2px solid rgb(114, 153, 114);
      border-radius: 5px;
      box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.2);
      padding: 0px 7px;
      .reportrange-text {
        border: none !important;
        .datePicker {
          border: none;
          box-shadow: none;
          font-size: 0.9rem;
          .mdi-calendar {
            padding-left: 10px;
          }
        }
      }
    }
    .daterangepicker.ltr.show-calendar.show-ranges.show-weeknumbers.opensleft.linked {
      min-width: 557px !important;
    }
  }
}
.dateSelectorHelpText {
  width: 100%;
  height: 200px;

  display: flex;
  align-items: center;
  justify-content: center;
  h2 {
    color: rgb(187, 185, 185);
  }
}
.confirmedText {
  font-weight: 600;
  background-color: rgb(0, 255, 0);
  color: white;
  font-size: 0.7rem;
  padding: 2px 10px;
  border-radius: 5px;
}
.notConfirmedText {
  font-weight: 600;
  background-color: rgb(255, 0, 0);
  color: white;
  font-size: 0.7rem;
  padding: 2px 10px;
  border-radius: 5px;
}
.nameLink {
  text-decoration: underline;
  color: rgb(0, 161, 73);
  font-weight: 600;
  cursor: pointer;
}

.button-separator {
  margin: 0 15px;
}

.dashboardFilterWrapper {
  margin-top: 3em;
}
</style>
